<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref, useTemplateRef } from 'vue'
import TabIcon from '../TabIcon.vue'
import { useNavigation } from '../useNavigation'
import type { Student2Context, Tail } from '@/types/student2'
import { useStaticAsset } from '@/composables/useStaticAsset'

const asset = useStaticAsset()

const props = defineProps<{ context: Student2Context; animalPicCount: number }>()
const { getLink, goto } = useNavigation()

const showMascot = ref(false)
const tailInfoModalText = ref<string | null>(null)
const container = useTemplateRef<HTMLElement>('container')

let observer: ResizeObserver | null = null

const smartbook = computed(() => props.context.smartbook ?? {})

const mascotFilename = computed(() => {
  if (!props.context.smartbook) return null
  const type = smartbook.value.scene
  const mood = props.context.student.tails.length ? 'sad' : 'happy'
  return asset(`img/student-v2/mascot_${type}_${mood}.png`)
})

const tailGroups = computed(() => {
  const result: Record<string, Tail[]> = {}

  for (const tail of props.context.student.tails) {
    const groupKey = `${tail.course_id}-${tail.teacher_user_id}`

    if (!result[groupKey]) result[groupKey] = []
    result[groupKey].push(tail)
  }

  const groups = Object.values(result)
  const currentCourseId = props.context.course_part.course_id

  groups.sort((a, b) => {
    if (a[0].course_id === currentCourseId) return -1
    if (b[0].course_id === currentCourseId) return 1
    return 0
  })

  return groups
})

const checkContainerSize = () => {
  if (!container.value) return

  const menuGridSize = 920
  const containerWidth = container.value.getBoundingClientRect().width

  const mascotSize = 160
  const mascotPadding = 24
  showMascot.value = containerWidth > menuGridSize + (mascotSize + mascotPadding) * 2
}

const getTailLink = (tail: Tail) => {
  if (tail.tail_type === 'control_item' && tail.custom_data?.song) {
    const song = tail.custom_data.song
    const songKey = typeof song === 'string' ? song : song.key
    return getLink({ tab: 'songs', hash: `song=${songKey}` })
  }

  if (tail.tail_type === 'workbook_task' && props.context.smartbook.development_state === 'full') {
    return getLink({ tab: 'roundup', unitId: tail.unit_id })
  }

  return undefined
}

const onLinkClick = (event: MouseEvent, tail: Tail) => {
  event.preventDefault()

  const href = (event.currentTarget as HTMLAnchorElement).href
  if (href) return goto(href)

  tailInfoModalText.value = `Задание юнита <b>${tail.junit_name}</b> не принято учителем`
}

const getAnimalNum = (tail: Tail) => {
  const tailTypeNum = ['control_item', 'workbook_task', 'workbook_accept'].indexOf(tail.tail_type) + 1
  const tailCode = Number(tail.code) || 0
  return ((tailCode + tail.junit_id + tailTypeNum) % props.animalPicCount) + 1
}

onMounted(() => {
  observer = new ResizeObserver(() => checkContainerSize())
  if (container.value) observer.observe(container.value)
  checkContainerSize()
})

onBeforeUnmount(() => {
  observer?.disconnect()
})
</script>

<template>
  <section ref="container" class="student-cabinet__page student-tails-page">
    <div v-if="showMascot && mascotFilename" class="student-tails-page__mascot">
      <img :src="mascotFilename" />

      <v-button mode="primary text block">
        {{ context.student.tails.length }}
        <img height="28" :src="asset('img/animals/animal-tail-11.png')" class="ml-1" />
      </v-button>
    </div>

    <v-rich-modal
      title="Хвост"
      :model-value="!!tailInfoModalText"
      @update:model-value="
        () => {
          tailInfoModalText = null
        }
      "
    >
      <p class="student-tails-page__tail-info-modal" v-html="tailInfoModalText" />
    </v-rich-modal>

    <ul class="student-tails-page__content">
      <ul
        v-for="tails in tailGroups"
        :key="`${tails[0].course_id}-${tails[0].teacher_user_id}`"
        class="student-tails-page__course-card"
      >
        <p class="d-flex align-center student-tails-page__course-card-info">
          <span class="text-title-bold">{{ tails[0].course_name }}</span>
          <span class="ml-1">{{ tails.length }}</span>
          <span class="flex-spacer"></span>
          <span class="ml-1">Учитель</span>
          <span class="ml-1 text-body-bold">{{ tails[0].teacher_name }}</span>
        </p>

        <a
          v-for="tail in tails"
          :key="`${tail.jstudent_id}-${tail.junit_id}-${tail.code}-${tail.tail_type}`"
          class="student-tails-page__tail-card"
          :href="getTailLink(tail)"
          @click="event => onLinkClick(event, tail)"
        >
          <TabIcon
            v-if="tail.tail_type === 'control_item' && tail.custom_data?.song"
            key="control"
            tab="songs"
            :size="24"
            class="student-tails-page__tail-type"
          />
          <TabIcon
            v-else-if="tail.tail_type === 'workbook_task'"
            key="roundup"
            tab="roundup"
            :size="24"
            class="student-tails-page__tail-type"
          />
          <v-icon v-else key="teacher" class="student-tails-page__tail-type blue-text" size="20"> mdi-school </v-icon>

          <img class="student-tails-page__tail-img" :src="asset(`img/animals/animal-tail-${getAnimalNum(tail)}.png`)" />

          <p v-text="tail.tail_name" />
        </a>
      </ul>
    </ul>
  </section>
</template>

<style lang="scss">
.student-tails-page {
  display: flex;
  align-items: center;
  justify-content: center;

  &__mascot {
    position: absolute;
    top: 100px;
    bottom: calc(50% + 100px);
    left: calc(((100% - 920px) / 4) - 80px);
    width: 160px;

    display: flex;
    flex-direction: column;
    gap: 24px;

    & > img {
      width: 160px;
      height: 160px;
    }
  }

  &__content {
    display: flex;
    flex-direction: column;
    gap: 16px;

    max-height: calc(100% - (var(--header_height, 0) * 2) - 24px);
    max-width: calc(100% - 24px);

    width: 920px;
    height: 600px;
  }

  &__course-card {
    border: 1px solid $lightGray;
    border-radius: 16px;
    padding: 16px 24px;

    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(130px, 1fr));
    gap: 16px;
  }

  &__course-card-info {
    grid-row: 1;
    grid-column: 1 / -1;
  }

  &__tail-card {
    height: 120px;
    background: #ffeaec;
    border-radius: 8px;
    padding: 0 16px;

    display: flex;
    flex-direction: column;
    gap: 16px;

    justify-content: center;
    align-items: center;

    font-size: 12px;
    font-weight: 700;
    line-height: 16px;
    text-align: center;

    position: relative;
    cursor: pointer;
  }

  &__tail-img {
    height: 64px;
    width: 80px;
    object-fit: contain;
    pointer-events: none;
  }

  &__tail-type {
    position: absolute;
    top: 8px;
    right: 8px;
  }
}
</style>
